import { CircularProgress } from "@mui/material";
import { useFormik } from "formik";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { connect, useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Input,
  Label,
  Modal,
  Row,
  Table,
} from "reactstrap";
import * as Yup from "yup";
import StyledButton from "../../components/Common/StyledButton";
import PublishConfirm from "../../components/CustomComponents/PublishConfirm";
import { getClientBranchListReq } from "../../service/branchService";
import { getAgreementItemsReq } from "../../service/itemService";
import { setBreadcrumbItems } from "../../store/Breadcrumb/actions";
import { changePreloader } from "../../store/actions";
import "../Orders/styles/CreateOrder.scss";
import "../Orders/styles/CreateOrderCard.scss";

import { HighlightOff } from "@mui/icons-material";
import { getClientWithIdReq } from "../../service/clientService";
import { createPurchaseRequisitionReq, getPurchaseRequisitionDetailsReq, getPurchaseRequisitionSequenceReq, updatePurchaseRequisitionReq } from "../../service/purchaseRequisitionService";


const getTitle = (id, copyId) => {
  if (copyId) return 'Copy Purchase Requisition';
  else if (id) return 'Edit Purchase Requisition';
  return 'Create Purchase Requisition';
};

const defaultTerms = `
  1. All the invoices should mandatorily be mentioned with Bansi Office Solutions Private Limited PO Number & should be attached with PO copy ,without which the invoice would be considered invalid.
  2. All the invoices should be mentioned with vendor bank details.
`;

const CreateRequisition = (props) => {
  let { id, copyId } = useParams();

  document.title = getTitle(id, copyId);

  let navigate = useNavigate();
  const dispatch = useDispatch();

  const [branchList, setBranchList] = useState([]);
  const effectCalled = useRef(false);
  const [rowData, setRowData] = useState([]);
  const [allTaxes, setAllTaxes] = useState([]);
  const [showRowData, setShowRowData] = useState(false);
  const [loadedData, setLoadedData] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredRowData, setFilteredRowData] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedQuantities, setSelectedQuantities] = useState({});
  const [selectedVariants, setSelectedVariants] = useState({});
  const [isButtonLoading, setIsButtonLoading] = useState(false);
  const [agreementId, setAgreementId] = useState(0);
  
  const [status, setStatus] = useState("draft");
  const [purchaseRequisitionNumber, setPurchaseRequisitionNumber] = useState("");
  const [publishModal, setPublishModal] = useState(false);
  const [terms, setTerms] = useState(defaultTerms);

  const [minDate, setMinDate] = useState(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0"); // Add leading zero if needed
    const day = String(today.getDate() + 1).padStart(2, "0"); // Add leading zero if needed
    return `${year}-${month}-${day}`;
  });

  const breadcrumbItems = [
    { title: "Dashboard", link: "/dashboard" },
    { title: "Purchase Requisition", link: "" },
    { title: getTitle(id, copyId), link: "#" },
  ];

  const bodyObject = {
    page: 1,
    limit: 200,
  };

  const columnDefs = ["Item Name", "HSN Code", "Category", "Quantity", "Unit", "",]

  const clientId = JSON.parse(localStorage.getItem("user")).clientId
  const [clientName, setClientName] = useState()

  const redirectToPurchaseRequisitionDetails = (id) => {
    let path = `/purchase-requisitions/${id}`;
    setTimeout(() => {
      navigate(path, id);
    }, 300);
  };

  const handleSearchQuery = (event) => {
    const query = event.target.value.toLowerCase();
    setSearchQuery(query);
    if (query.length > 0) {
      const filteredData = rowData.filter((item) =>
        item.title.toLowerCase().includes(query)
      );
      setFilteredRowData(filteredData);
      setShowRowData(true);
    } else {
      setShowRowData(false);
    }
  };

  const getBranchData = useCallback(async () => {
    dispatch(changePreloader(true));
    const response = await getClientBranchListReq();
    if (response) {
      setBranchList(response?.payload?.branches);
    }
    dispatch(changePreloader(false));
  }, [dispatch]);

  const handleStatusChange = (event) => {
    setStatus(event.target.value);
  };

  const handleChangeDate = (event) => {
    const selectedDate = new Date(event.target.value);
    const today = new Date(minDate);

    // Check if the selected date is before today
    if (selectedDate < today) {
      // Reset to today's date
      event.target.value = minDate;
    }
    formik.setFieldValue("deliveryDate", event.target.value);
  };

  const formik = useFormik({
    initialValues: {
      purchaseRequisitionNumber: "",
      billingAddress: "",
      shippingAddress: "",
      deliveryDate: "",
      terms: terms
    },
    validationSchema: Yup.object({
      purchaseRequisitionNumber: Yup.string().required("Please Enter Purchase Requisition Number"),
      billingAddress: Yup.string().required("Please Select Billing Address"),
      shippingAddress: Yup.string().required("Please Select Shipping Address"),
      deliveryDate: Yup.string().optional(),
      terms: Yup.string().optional()
    }),
    onSubmit: (values) => {
      saveOrUpdatePurchaseRequisition(values);
    },
  });

  const getListOfRowData = async () => {
    try {
      const { items, taxes } = await getAgreementItemsReq(bodyObject);
      setAgreementId(items[0]?.agreementId);
      setAllTaxes(taxes);
      if (items) {
        setRowData(items);
      } else {
        console.error("Invalid response structure", items);
      }
    } catch (error) {
      console.error("Error fetching agreement items", error);
    } finally {
      setLoadedData(true);
    }
  };

  const fetchClientData = async () => {
    try {
      const data = { _id: clientId };
      const res = await getClientWithIdReq(data);

      setClientName(res.payload.client.name)

    } catch (error) {
      console.error("Error fetching user data:", error);
    }
  };

  const fetchPurchaseRequisitionSequence = async () => {
    try {
      if (!id) {
        const res = await getPurchaseRequisitionSequenceReq(); 
        // set the PR Number
        setPurchaseRequisitionNumber(res.sequence);
      }
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
  };

  const handleItemSelect = (item, variant, values) => {
    const newItem = {
      itemId: item._id,
      zohoItemId: item.zohoItemId,
      itemName: item.title,
      itemDescription:item.title,
      alternateItemTitle:item.alternateItemTitle,
      itemType: item.itemType,
      itemUnit: item.itemUnit,
      hsnCode: item.hsnCode,
      taxPreference: item.taxPreference,
      category: item.category.name,
      unitPrice: variant.price,
      quantity: 1,
      variantId: variant._id,
      taxes: item.taxes,
    };

    if (
      !selectedItems.some(
        (selectedItem) =>
          selectedItem.itemId === newItem.itemId &&
          selectedItem.variantId === variant._id&&selectedItem.alternateItemTitle===newItem.alternateItemTitle
      )
    ) {
      setSelectedItems([...selectedItems, newItem]);
      setSelectedQuantities({
        ...selectedQuantities,
        [`${item._id}-${variant._id}-${item.alternateItemTitle}`]: "1",
      });
      setSelectedVariants({ ...selectedVariants, [item._id]: variant._id });
    }
    setShowRowData(false);
  };

  const handleItemDelete = (itemId, variantId,alternateItemTitle) => {
    const updatedItems = selectedItems.filter(
      (item) => item.itemId !== itemId&&item.alternateItemTitle===alternateItemTitle || item.variantId !== variantId&&item.alternateItemTitle!==alternateItemTitle
    );
    setSelectedItems(updatedItems);
    const updatedQuantities = { ...selectedQuantities };
    delete updatedQuantities[`${itemId}-${variantId}-${alternateItemTitle}`];
    setSelectedQuantities(updatedQuantities);
    const updatedVariants = { ...selectedVariants };
    delete updatedVariants[itemId];
    setSelectedVariants(updatedVariants);
  };

  // Render selected items in a table
  const renderSelectedItems = () => {
    if (!selectedItems || selectedItems.length === 0) {
      return (
        <tr>
          <td colSpan={8}>
            <p className="m-2 text-center">No items Added.</p>
          </td>
        </tr>
      );
    }

    return selectedItems.map((item) => {
      return (
        <tr
          key={`${item.itemId}-${item.variantId}`}
          style={{ verticalAlign: "start" }}
        >
          <td>
            <div>{item.alternateItemTitle||item.itemName}</div>
          </td>
          <td>{item.hsnCode}</td>
          <td style={{ whiteSpace: "nowrap" }}>{item.category}</td>
          <td style={{ width: "10%" }}>
            <Input
              type="number"
              min="1"
              required
              value={selectedQuantities[`${item.itemId}-${item.variantId}-${item.alternateItemTitle}`] ?? ""}
              onChange={(e) => {
                const value = e.target.value;
                const key = `${item.itemId}-${item.variantId}-${item.alternateItemTitle}`;

                setSelectedQuantities({
                  ...selectedQuantities,
                  [key]: value,
                });
              }}
              onInvalid={(e) => {
                const value = e.target.value;
                const quantity = Number(value);
                if (!value || Number.isNaN(quantity) || quantity < 1) {
                  e.target.setCustomValidity("Quantity should be greater than 0");
                } else {
                  e.target.setCustomValidity("");
                }
              }}
              onInput={(e) => {
                e.target.setCustomValidity("");
              }}
            />
          </td>
          <td style={{ whiteSpace: "nowrap" }}>{item.itemUnit}</td>
          <td>
            <HighlightOff
              color="error"
              style={{ cursor: "pointer" }}
              onClick={() => handleItemDelete(item.itemId, item.variantId,item.alternateItemTitle)}
            />
          </td>
        </tr>
    )});
  };

  useEffect(() => {
    (id || copyId) && rowData && getPurchaseRequisitionDetails(id || copyId);
  }, [id, copyId, rowData]);

  useEffect(() => {
    return () => {
      formik.resetForm();
      setSelectedItems([]);
    };
  }, [id, copyId]); // only reset when navigating to a different PR/copy, not on every rowData fetch

  const getPurchaseRequisitionDetails = async (id) => {
    const response = await getPurchaseRequisitionDetailsReq(id);
    const purchaseRequisition = response?.data;
    let PRNumber = purchaseRequisitionNumber;
    let finalDeliveryDate = purchaseRequisition.deliveryDate;
    if (!copyId) {
      setPurchaseRequisitionNumber(purchaseRequisition.purchaseRequisitionNumber);
    }
    if (copyId && !PRNumber) {
      const res = await getPurchaseRequisitionSequenceReq(); 
      PRNumber = res.sequence;
      setPurchaseRequisitionNumber(PRNumber);
    }
    if (copyId) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const deliveryDate = new Date(purchaseRequisition.deliveryDate);
      deliveryDate.setHours(0, 0, 0, 0);

      finalDeliveryDate = deliveryDate > today ? deliveryDate : "";
    }

    formik.setValues({
      billingAddress: purchaseRequisition.billing.branchId,
      shippingAddress: purchaseRequisition.shipping.branchId,
      purchaseRequisitionNumber: copyId ? PRNumber : purchaseRequisition.purchaseRequisitionNumber,
      deliveryDate: finalDeliveryDate ? formatDate(finalDeliveryDate) : "",
      terms: purchaseRequisition.terms ? purchaseRequisition.terms : terms
    });
    purchaseRequisition.terms && setTerms(purchaseRequisition.terms)

    let items = purchaseRequisition.items;

    const newItems = items.map((item) => {
      const {
        alternateItemTitle,
        variantId,
        zohoItemId,
        unitPrice,
        quantity,
        taxes,
        itemId: { description, title, hsnCode, itemUnit, itemType, _id, taxes: itemTaxes },
      } = item;
      return {
        hsnCode,
        itemDescription:
          description && description !== "Sample Description"
            ? description
            : title,
        itemId: _id,
        itemName: title,
        itemType,
        itemUnit,
        quantity,
        taxes: taxes.map((tax) => ({ ...tax.parent})),
        itemTaxes: itemTaxes,
        alternateItemTitle:alternateItemTitle||description,
        unitPrice,
        variantId,
        zohoItemId,
        category: rowData.find((data) => data._id === _id)?.category.name,
      };
    });


    const result = newItems.reduce((acc, item) => {
      const key = `${item.itemId}-${item.variantId}-${item.alternateItemTitle}`;
      acc[key] = String(item.quantity ?? "");
      return acc;
    }, {});

    setSelectedQuantities(result);
    setSelectedItems(newItems);
  };

  function formatDate(dateStr) {
    const date = new Date(dateStr);

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are zero-based
    const year = date.getFullYear();

    return `${year}-${month}-${day}`;
  }

  useEffect(() => {
    props.setBreadcrumbItems(getTitle(id, copyId), breadcrumbItems);
    if (!effectCalled.current) {
      getBranchData();
      getListOfRowData();
      fetchPurchaseRequisitionSequence();
      fetchClientData();
      effectCalled.current = true;
    }
  }, [props, breadcrumbItems, getBranchData]);

  useEffect(() => {
    formik.setFieldValue("purchaseRequisitionNumber", purchaseRequisitionNumber)
  }, [purchaseRequisitionNumber])
    
  
  useEffect(() => {
    formik.setFieldValue("terms", terms)
  }, [terms])

  const getFilteredTaxes = (taxes, itemTaxes, sameState) => {
    let taxId = taxes.find((t) => t.taxSpecification === (sameState ? "intra" : "inter"))?.taxId;
    if (!taxId) {
      taxId = itemTaxes.find((t) => t.taxSpecification === (sameState ? "intra" : "inter"))?.taxId;
    }
    const filterTaxes = allTaxes.find((tax) => tax._id === taxId);
    return filterTaxes.taxes.map((tax) => ({
      taxId: tax._id,
      name: tax.name,
      rate: tax.rate,
      parent: {
        taxSpecification: sameState ? "intra" : "inter",
        taxId: filterTaxes._id,
        name: filterTaxes.name,
        rate: filterTaxes.totalRate
      },
    }));
  }

  const saveOrUpdatePurchaseRequisition = (values) => {
    console.log(values);
    const hasInvalidQty = Object.values(selectedQuantities).some((qty) => {
      const n = Number(qty);
      return !qty || Number.isNaN(n) || n < 1;
    });
    
    if (hasInvalidQty) {
      toast.error("Quantity should be greater than 0");
      return;
    }
    if (selectedItems.length === 0) {
      toast.error("Please select at least 1 item!", {
        position: "top-center",
        theme: "light",
      });
      return;
    } else {
      // setIsButtonLoading(true);
      // e.preventDefault();

      async function handlePurchaseRequisitionCreation() {
        try {
          const response = id
            ? await updatePurchaseRequisitionReq(body)
            : await createPurchaseRequisitionReq(body);

          if (response.success) {
            const _id = response?.payload?.data?._id;
            setTimeout(() => {
              redirectToPurchaseRequisitionDetails(_id);
            }, 1000);
            toast.success(
              id
                ? "Purchase Requisition Updated Successfully"
                : "Purchase Requisition Created Successfully",
              {
                position: "top-center",
                theme: "light",
              }
            );
          } else {
            const { message } = response;
            toast.error(message || 'Something went wrong !!');
          }
        } catch (error) {
          toast.error(error.response.data.message, {
            position: "top-center",
            theme: "light",
          });
        } finally {
          setIsButtonLoading(false);
        }
      }
      // validation.handleSubmit();
      const {
        deliveryDate,
        billingAddress,
        shippingAddress,
        terms
      } = values;

      const normalizedDeliveryDate =
        deliveryDate && deliveryDate.trim() !== ""
          ? deliveryDate
          : "";
  
      const billingBranch = branchList.find((branch) => branch._id === billingAddress);
      const shippingBranch = branchList.find((branch) => branch._id === shippingAddress);

      const sameState = billingBranch?.associatedWarehouse?.gstIn.substring(0,2) === billingBranch?.gstin?.taxRegistrationNumber.substring(0,2);
      const body = {
        agreementId,
        status,
        deliveryDate: normalizedDeliveryDate,
        billingBranchId: billingAddress,
        shippingBranchId: shippingAddress,
        items: selectedItems.map(({ category, itemTaxes, gst, _id, ...rest }) => ({
          ...rest,
          quantity:
            Number(selectedQuantities[`${rest.itemId}-${rest.variantId}-${rest.alternateItemTitle}`]) ||
            rest.quantity,
          taxes: getFilteredTaxes(rest.taxes, itemTaxes, sameState),
        })),
        terms
      };

      if (id) {
        body.purchaseOrderId = id;
      }

      handlePurchaseRequisitionCreation(body);
      return false;
    }
  };

  const handleClickPR = () => {
    const purchaseRequisitionNumber = formik.values.purchaseRequisitionNumber;
    if (!purchaseRequisitionNumber) {
      formik.setFieldValue("purchaseRequisitionNumber", purchaseRequisitionNumber);
    }
  };

  const handleChangeTerms = (e) => {
    let value = e.target.value;
    formik.setFieldValue("terms", value)
    setTerms(value)
  }

  const getAddressValue = (branch) => {
    if(!branch?.address) return '';
    return <>
    {branch?.address.addressLine1},<br/>
    {branch?.address.addressLine2 || ''},<br />
    {branch?.address.state}&nbsp;{branch?.address.city}&nbsp;-&nbsp;{branch?.address.pincode}.
    </>;
  }

  const billingAddress = branchList.find((branch) => branch._id === formik.values.billingAddress);
  const shippingAddress = branchList.find((branch) => branch._id === formik.values.shippingAddress);
  const defaultBranch = billingAddress;

  return (
    <form onSubmit={formik.handleSubmit} autoComplete="off">
      <div style={{ position: "relative" }}>
        <div
          style={{
            position: "absolute",
            top: -50,
            right: 10,
            display: "flex",
          }}
        >
          <Modal size="sm" isOpen={publishModal} centered={true}>
            <PublishConfirm
              setPublishModal={setPublishModal}
              setStatus={setStatus}
              id={id}
            />
          </Modal>
          <select
            className="form-select focus-width"
            name="status"
            value={status}
            onChange={handleStatusChange}
          >
            <option value="draft">Draft</option>
            <option value="published">Published</option>
          </select>
          <StyledButton
            color={"primary"}
            type="submit"
            className={"w-md mx-2"}
            isLoading={isButtonLoading}
          >
            {id ? "Update" : "Save"}
          </StyledButton>
        </div>
      </div>
      <Card>
        <CardBody>
          <div className="card-content">
            <div className="details">
              <h3 className="fw-bolder">
                {clientName}
              </h3>
              <p className="m-0">
                {defaultBranch?.name}
              </p>
              <p className="m-0">
                {formik.values.billingAddress ?
                  getAddressValue(billingAddress)
                  : getAddressValue(defaultBranch)
                }
              </p>
              <p className="m-0">
                {defaultBranch?.contact
                }
              </p>
            </div>
            <div>
              <span className="purchase-order">Purchase Requisition</span>
              <br />
              <span className="purchase-order-no">
                {purchaseRequisitionNumber}
              </span>
            </div>
          </div>
        </CardBody>
      </Card>
      <Row className="mb-3">
        <div style={{ position: "relative" }}>
          <Row className="equal-height-cards">
            <Col xl="8">
              <Card>
                <CardHeader>Billing & Shipping</CardHeader>
                <CardBody>
                  <div className="d-flex">
                    <div className="p-2" style={{ width: "49%" }}>
                      <div>
                        <label className="col-form-label">
                          Billing Address
                        </label>
                        <select
                          name="billingAddress"
                          id="billingAddress"
                          value={formik.values.billingAddress}
                          placeholder="Please Select Billing Address"
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          className="form-select focus-width"
                        >
                          <option value="" disabled>
                            Select Billing Address
                          </option>
                          {branchList.map((branch) => (
                            <option key={branch._id} value={branch._id}>
                              {branch.name}
                            </option>
                          ))}
                        </select>
                        {formik.touched.billingAddress &&
                        formik.errors.billingAddress ? (
                          <div className="text-danger">
                            {formik.errors.billingAddress}
                          </div>
                        ) : null}
                        <h5 className="my-3">
                          {
                            getAddressValue(billingAddress)
                          }
                        </h5>
                      </div>
                    </div>
                    <div className="p-2">
                      <div className="vr h-100"></div>
                    </div>
                    <div className="p-2" style={{ width: "49%" }}>
                      <div>
                        <label className="col-form-label">Shipping</label>
                        <select
                          name="shippingAddress"
                          id="shippingAddress"
                          value={formik.values.shippingAddress}
                          placeholder="Please Select Shipping Address"
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          className="form-select focus-width"
                        >
                          <option value="" disabled>
                            Select Shipping Address
                          </option>
                          {branchList.map((branch) => (
                            <option key={branch._id} value={branch._id}>
                              {branch.name}
                            </option>
                          ))}
                        </select>
                        {formik.touched.shippingAddress &&
                        formik.errors.shippingAddress ? (
                          <div className="text-danger">
                            {formik.errors.shippingAddress}
                          </div>
                        ) : null}
                        <h5 className="my-3">
                          {
                            getAddressValue(shippingAddress)
                          }
                        </h5>
                      </div>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
            <Col xl="4">
              <Card>
                <CardHeader>Requisition Info</CardHeader>
                <CardBody>
                  <Row>
                    <div>
                      <Label htmlFor="purchaseRequisitionNumber">
                        Purchase Requisition Number
                      </Label>
                      <Input
                        type="text"
                        name="purchaseRequisitionNumber"
                        id="purchaseRequisitionNumber"
                        value={purchaseRequisitionNumber}
                        placeholder="PR/BLR/#123"
                        onClick={handleClickPR}
                        onBlur={formik.handleBlur}
                        disabled={true}
                      />
                      {formik.touched.purchaseRequisitionNumber &&
                      formik.errors.purchaseRequisitionNumber ? (
                        <div className="text-danger">
                          {formik.errors.purchaseRequisitionNumber}
                        </div>
                      ) : null}
                    </div>
                  </Row>
                  <Row>
                    <div className="mt-3 mb-0">
                      <Label htmlFor="deliveryDate">
                        Expected Delivery Date
                      </Label>
                      <Input
                        type="date"
                        name="deliveryDate"
                        id="deliveryDate"
                        placeholder="dd-mm-yyyy"
                        value={formik.values.deliveryDate}
                        min={minDate}
                        onClick={(e) => e.target.showPicker()}
                        onChange={handleChangeDate}
                        onBlur={formik.handleBlur}
                      />
                      {formik.touched.deliveryDate &&
                      formik.errors.deliveryDate ? (
                        <div className="text-danger">
                          {formik.errors.deliveryDate}
                        </div>
                      ) : null}
                    </div>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
          <Row>
            <Col xl="8">
              <Card className="mt-3" style={{ height: "fit-content" }}>
                <CardHeader>Requisition Details</CardHeader>
                <CardBody>
                  <div
                    style={{ display: "flex", justifyContent: "space-between" }}
                  ></div>
                  <div>
                    <Row>
                      <Col xl="12">
                        <Input
                          id="searchQuery"
                          name="searchQuery"
                          placeholder="Search Item"
                          className="form-control m-1"
                          value={searchQuery}
                          onChange={handleSearchQuery}
                          autoComplete="off"
                        />
                      </Col>
                    </Row>

                    {showRowData && (
                      <div
                        className="form-control"
                        style={{
                          position: "absolute",
                          zIndex: 4,
                          width: "750px",
                          overflowY: "auto",
                        }}
                      >
                        <Table hover striped>
                          <thead>
                            <tr>
                              <th>Item Title</th>
                              <th>HSN Code</th>
                              <th>Category</th>
                            </tr>
                          </thead>
                          <tbody>
                            {!loadedData && (
                              <tr>
                                <td
                                  colSpan={5}
                                  className="form-text-lg text-center"
                                >
                                  <CircularProgress />
                                </td>
                              </tr>
                            )}
                            {filteredRowData.map((item) =>
                              item.variants.map((variant) => (
                                <tr
                                  key={variant._id}
                                  className="row-clickable cursor-pointer"
                                  onClick={() => {
                                    handleItemSelect(item, variant, {
                                      billingAddress,
                                      shippingAddress,
                                    });
                                    setSearchQuery("");
                                  }}
                                >
                                  <td>{item.alternateItemTitle||item.title}</td>
                                  <td>{item.hsnCode}</td>
                                  <td>{item.category.name}</td>
                                </tr>
                              ))
                            )}
                            {(!filteredRowData ||
                              filteredRowData.length === 0) && (
                              <tr>
                                <td
                                  colSpan={5}
                                  className="form-text-lg text-center"
                                >
                                  No items found.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </Table>
                      </div>
                    )}
                  </div>
                  <Row className="mt-1 table-responsive fw-medium">
                    <Table hover striped>
                      <thead>
                        <tr>
                          {columnDefs.map((col, index) => <th key={`col-${col}-${index}`}>{col}</th>)}
                        </tr>
                      </thead>
                      <tbody>{renderSelectedItems()}</tbody>
                    </Table>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
          <Row>
            <Col xl="8">
              <Card>
                <CardBody>
                  <Label htmlFor="terms">
                    Terms & Conditions
                  </Label>
                  <Input
                    type="textarea"
                    name="terms"
                    id="terms"
                    value={terms}
                    placeholder="Terms & Conditions"
                    onChange={handleChangeTerms}
                    onBlur={formik.handleBlur}
                    rows={10}
                  />
                  {formik.touched.terms &&
                  formik.errors.terms ? (
                    <div className="text-danger">
                      {formik.errors.terms}
                    </div>
                  ) : null}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </Row>
    </form>
  );
};

export default connect(null, { setBreadcrumbItems })(CreateRequisition);
